import { useCallback } from 'react';
import { create } from 'zustand';
import type { Project, ProjectRole } from '@shared/types/factory';
import type { ProjectIconName } from '@shared/project-icon';
import { formatTaskNumber } from '@shared/task-number';

/**
 * The project the URL is in (`/p/:slug/…`), set by the project layout once GET /api/project has
 * answered (pages/project/ProjectLayout.tsx), and cleared when the tab leaves it. `fetchApi` names
 * it in the X-Project-Id header for every call made meanwhile (a header the caller passes wins),
 * and the shell's project parts (the live socket, notifications, the Tasks panel) follow it.
 *
 * Not persisted: the URL is the source; a reload sets it again.
 */
export interface CurrentProject {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  /** Hex colour: the top bar's tint and the sidebar icon (T-18). */
  color: string;
  /** Two letters in front of its task refs: SF-T-42 (T-25). */
  task_prefix: string;
  /** Its sidebar icon (T-25): a PROJECT_ICONS name, or null for the default. */
  icon: ProjectIconName | null;
  /** The caller's effective role (admins are maintainers everywhere). */
  role: ProjectRole;
}

interface CurrentProjectState {
  project: CurrentProject | null;
}

export const useCurrentProjectStore = create<CurrentProjectState>()(() => ({ project: null }));

type ProjectChangeListener = (next: CurrentProject | null, previous: CurrentProject | null) => void;
const listeners = new Set<ProjectChangeListener>();

/**
 * Called when the tab moves to another project or leaves projects (not for a role or name change
 * within the same one). The task store resets on it, so one project's tasks never show in another.
 */
export function onProjectChange(listener: ProjectChangeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCurrentProject(): CurrentProject | null {
  return useCurrentProjectStore.getState().project;
}

export function toCurrentProject(project: Project, role: ProjectRole): CurrentProject {
  return { id: project.id, slug: project.slug, name: project.name, timezone: project.timezone, color: project.color, task_prefix: project.task_prefix, icon: project.icon, role };
}

/** A leave waiting to happen (clearCurrentProject); a new project set first cancels it. */
let pendingClear: ReturnType<typeof setTimeout> | null = null;

function cancelPendingClear(): void {
  if (pendingClear) clearTimeout(pendingClear);
  pendingClear = null;
}

export function setCurrentProject(next: CurrentProject | null): void {
  cancelPendingClear();
  const previous = getCurrentProject();
  if (
    previous === next ||
    (previous &&
      next &&
      previous.id === next.id &&
      previous.slug === next.slug &&
      previous.name === next.name &&
      previous.timezone === next.timezone &&
      previous.color === next.color &&
      previous.task_prefix === next.task_prefix &&
      previous.icon === next.icon &&
      previous.role === next.role)
  ) {
    return;
  }
  useCurrentProjectStore.setState({ project: next });
  if ((previous?.id ?? null) !== (next?.id ?? null)) {
    for (const listener of [...listeners]) listener(next, previous);
  }
}

/**
 * Leave the project, unless the tab has already moved on to another one. Deferred a moment: moving
 * between a project's pages remounts its layout (each page has its own error boundary), and the
 * layout setting the same project again cancels the leave, so the live socket and the task store
 * aren't reset on every click.
 */
export function clearCurrentProject(projectId: string): void {
  cancelPendingClear();
  pendingClear = setTimeout(() => {
    pendingClear = null;
    if (getCurrentProject()?.id === projectId) setCurrentProject(null);
  }, 0);
}

/** The header value for the current project (its id), or null outside a project. */
export function currentProjectHeader(): string | null {
  return getCurrentProject()?.id ?? null;
}

export function useCurrentProject(): CurrentProject | null {
  return useCurrentProjectStore((s) => s.project);
}

/** The caller's role on the current project, or null outside one. */
export function useProjectRole(): ProjectRole | null {
  return useCurrentProjectStore((s) => s.project?.role ?? null);
}

/**
 * A task number as people see it in the current project: SF-T-42 (formatTaskNumber), or T-42
 * outside a project. Re-renders when the project's prefix changes.
 */
export function useFormatTaskRef(): (number: number | null | undefined) => string {
  const prefix = useCurrentProjectStore((s) => s.project?.task_prefix ?? null);
  return useCallback((number: number | null | undefined) => formatTaskNumber(number, prefix), [prefix]);
}

/** The current project's task prefix (SF), or null outside a project; for code outside React. */
export function currentTaskPrefix(): string | null {
  return getCurrentProject()?.task_prefix ?? null;
}
